import { compact, keyBy } from "lodash";
import moment from "moment-timezone";

import type * as types from "../constants";

import {
  convertEndDateToTimeZone,
  convertStartDateToTimeZone,
  orderByDate,
  shiftISODate,
} from "./util";

import { ISO_DATE_FORMAT } from ".";

export function* iterateOverInterval(interval: types.DateInterval) {
  const { startDate, endDate } = interval;
  for (let date = startDate; date <= endDate; date = shiftISODate(date, +1)) {
    yield date;
  }
}

export function* iterateHourlyOverInterval(interval: types.DateInterval) {
  for (const date of iterateOverInterval(interval)) {
    for (let i = 0; i < 24; ++i) {
      yield `${date}T${i.toString().padStart(2, "0")}`;
    }
  }
}

export function* iterateHourlyOverIntervalWithAdditionalAccuracy(
  interval: types.DateInterval,
  timezone: string,
) {
  for (const date of iterateHourlyOverInterval(interval)) {
    yield moment.tz(date, timezone).toISOString();
  }
}

export function isWithinDateInterval(
  date: string,
  interval: types.DateInterval,
) {
  return date >= interval.startDate && date <= interval.endDate;
}

export function isWithinDateIntervalHourly(
  date: string,
  interval: types.DateInterval,
  timezone: string,
) {
  return (
    new Date(date) >=
      convertStartDateToTimeZone(interval.startDate, timezone) &&
    new Date(date) <= convertEndDateToTimeZone(interval.endDate, timezone)
  );
}

export function monthIntervalToDateInterval(
  mi: types.MonthInterval,
): types.DateInterval {
  const { startMonth, endMonth } = mi;
  const startDate = `${startMonth}-01`;
  const endDate = moment(endMonth, "YYYY-MM", true)
    .endOf("month")
    .format("YYYY-MM-DD");
  return { startDate, endDate };
}

export function monthIntervalWidth(mi: types.MonthInterval): number {
  const { startMonth, endMonth } = mi;
  return 1 + monthToIndex(endMonth) - monthToIndex(startMonth);
}

export function monthToIndex(m: string) {
  const [year, month] = m.split("-").map(parseFloat);
  return 12 * year + (month - 1);
}

export function indexToMonth(i: number) {
  const year = Math.floor(i / 12)
    .toString()
    .padStart(4, "0");
  const month = ((i % 12) + 1).toString().padStart(2, "0");
  return `${year}-${month}`;
}

export function* iterateOverMonthInterval(interval: types.MonthInterval) {
  const { startMonth, endMonth } = interval;
  const start = monthToIndex(startMonth);
  const end = monthToIndex(endMonth);
  for (let index = start; index <= end; ++index) {
    yield indexToMonth(index);
  }
}

export function monthIntervalToArray(interval: types.MonthInterval): string[] {
  return [...iterateOverMonthInterval(interval)];
}

interface HasDate {
  date: string;
}

export function* iterateDataByDayInterval<T extends HasDate>(
  dateData: T[],
  interval: number,
  startDate?: string,
) {
  if (dateData.length === 0) {
    yield {
      dateRange: { startDate: startDate ?? "", endDate: startDate ?? "" },
      data: [] as T[],
    };
    return;
  }
  const sortedDateData = orderByDate(dateData, (obj) => obj.date);
  const minDate = startDate || sortedDateData[0].date;
  const maxDate = sortedDateData[sortedDateData.length - 1].date;
  const dateDataByDate = keyBy(dateData, "date");
  let currentDate = minDate;
  while (currentDate <= maxDate) {
    const endDate = moment(currentDate)
      .add(interval - 1, "days")
      .format(ISO_DATE_FORMAT);
    const dateRange = { startDate: currentDate, endDate };
    const daysInRange = [...iterateOverInterval(dateRange)];
    yield {
      dateRange,
      data: compact(daysInRange.map((date) => dateDataByDate[date])),
    };
    currentDate = moment(endDate).add(1, "day").format(ISO_DATE_FORMAT);
  }
}

export function groupByDateRangesKeyable<T extends HasDate>(
  datedData: T[],
  dateRanges: { startDate: string; endDate: string }[],
) {
  const datedDataByDate = keyBy(datedData, "date");
  const groupedData: Record<string, Record<string, T[]>> = {};
  dateRanges.forEach((dateRange) => {
    const datesByDay = [...iterateOverInterval(dateRange)];
    const group = compact(datesByDay.map((date) => datedDataByDate[date]));
    groupedData[dateRange.startDate] = {};
    groupedData[dateRange.startDate][dateRange.endDate] = group;
  });

  return groupedData;
}

export function groupByDateRanges<T extends HasDate>(
  datedData: T[],
  dateRanges: { startDate: string; endDate: string }[],
) {
  const keyableGroups = groupByDateRangesKeyable(datedData, dateRanges);
  return dateRanges.map(
    (dateRange) =>
      keyableGroups[dateRange.startDate]?.[dateRange.endDate] ?? [],
  );
}
